import { HierarchyLevelType, IDesign } from 'common/interfaces/hierarchy-entities';
import { DESIGN_ANCESTOR_LEVELS, TAncestorLevel } from './design-sync.utils';

export type TSyncTreeNode = {
  id: string,
  name: string,
  level: HierarchyLevelType,
  children: TSyncTreeNode[],
  designIds: string[],
  design?: IDesign,
};

const toDesignNode = (design: IDesign): TSyncTreeNode => {
  return {
    id: design.designid,
    name: design.name,
    level: HierarchyLevelType.Design,
    children: [],
    designIds: [design.designid],
    design,
  };
};

// non-orphaned designs always carry every ancestor level
const getAncestor = (design: IDesign, level: TAncestorLevel) => {
  return design.metadata.hierarchyData![level]!;
};

const groupByAncestor = (designs: IDesign[], level: TAncestorLevel) => {
  const designsByAncestorId = new Map<string, IDesign[]>();
  designs.forEach((design) => {
    const ancestorId = getAncestor(design, level).id;
    const group = designsByAncestorId.get(ancestorId) ?? [];
    group.push(design);
    designsByAncestorId.set(ancestorId, group);
  });
  return designsByAncestorId;
};

const groupByLevels = (designs: IDesign[], levels: TAncestorLevel[]): TSyncTreeNode[] => {
  if (!levels.length) {
    return designs.map(toDesignNode);
  }
  const [level, ...lowerLevels] = levels;
  const designsByAncestorId = groupByAncestor(designs, level);
  return Array.from(designsByAncestorId, ([ancestorId, groupDesigns]) => {
    return {
      id: ancestorId,
      name: getAncestor(groupDesigns[0], level).name,
      level,
      children: groupByLevels(groupDesigns, lowerLevels),
      designIds: groupDesigns.map((design) => design.designid),
    };
  });
};

export const buildSyncTree = (designs: IDesign[], entityType: TAncestorLevel) => {
  const levelsBelow = DESIGN_ANCESTOR_LEVELS.slice(DESIGN_ANCESTOR_LEVELS.indexOf(entityType) + 1);
  return groupByLevels(designs, levelsBelow);
};

const filterNodes = (nodes: TSyncTreeNode[], term: string): TSyncTreeNode[] => {
  return nodes.flatMap((node) => {
    if (node.name.toLowerCase().includes(term)) {
      return [node];
    }
    const children = filterNodes(node.children, term);
    if (!children.length) {
      return [];
    }
    return [{ ...node, children, designIds: children.flatMap((child) => child.designIds) }];
  });
};

export const filterSyncTree = (nodes: TSyncTreeNode[], searchTerm: string) => {
  const term = searchTerm.trim().toLowerCase();
  return term ? filterNodes(nodes, term) : nodes;
};
